import { useEffect, useMemo, useRef } from "react";
import { BarChart, type BarSeriesOption } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  type GridComponentOption,
  type TooltipComponentOption,
} from "echarts/components";
import * as echarts from "echarts/core";
import type { ComposeOption } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { formatDate } from "@/lib/datetime";
import { readThemeToken, type ResolvedTheme } from "@/lib/theme";
import { useThemeStore } from "@/store/themeStore";
import type { HousePresenceDailyBucket } from "@/types";

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

type PresenceChartOption = ComposeOption<
  BarSeriesOption | GridComponentOption | TooltipComponentOption
>;

interface ChartPalette {
  bar: string;
  label: string;
  axisLine: string;
  splitLine: string;
  tooltipBackground: string;
  tooltipBorder: string;
  tooltipText: string;
}

type ChartTokens = Record<keyof ChartPalette, `--${string}`>;

/** Dark mode needs one step more contrast on lines and borders. */
const CHART_TOKENS: Record<ResolvedTheme, ChartTokens> = {
  light: {
    bar: "--color-primary-500",
    label: "--color-surface-500",
    axisLine: "--color-surface-200",
    splitLine: "--color-surface-100",
    tooltipBackground: "--color-surface-0",
    tooltipBorder: "--color-surface-200",
    tooltipText: "--color-surface-900",
  },
  dark: {
    bar: "--color-primary-400",
    label: "--color-surface-500",
    axisLine: "--color-surface-300",
    splitLine: "--color-surface-200",
    tooltipBackground: "--color-surface-0",
    tooltipBorder: "--color-surface-300",
    tooltipText: "--color-surface-900",
  },
};

const POINTER_SHADOW_OPACITY = 0.05;

/** echarts draws on canvas, so `theme.css` tokens are resolved to concrete colors. */
function readChartPalette(theme: ResolvedTheme): ChartPalette {
  const tokens = CHART_TOKENS[theme];
  return {
    bar: readThemeToken(tokens.bar),
    label: readThemeToken(tokens.label),
    axisLine: readThemeToken(tokens.axisLine),
    splitLine: readThemeToken(tokens.splitLine),
    tooltipBackground: readThemeToken(tokens.tooltipBackground),
    tooltipBorder: readThemeToken(tokens.tooltipBorder),
    tooltipText: readThemeToken(tokens.tooltipText),
  };
}

interface PresenceOccupancyChartProps {
  buckets: HousePresenceDailyBucket[];
  ariaLabel: string;
}

function formatBucketDate(date: string): string {
  return formatDate(`${date}T00:00:00`, {
    month: "short",
    day: "numeric",
    year: undefined,
  });
}

export function PresenceOccupancyChart({
  buckets,
  ariaLabel,
}: PresenceOccupancyChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedTheme = useThemeStore((s) => s.resolved);

  const option = useMemo<PresenceChartOption>(() => {
    const palette = readChartPalette(resolvedTheme);
    return {
      animation: false,
      textStyle: { fontFamily: readThemeToken("--font-sans") },
      grid: {
        left: 40,
        right: 16,
        top: 16,
        bottom: 48,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
          shadowStyle: {
            color: palette.tooltipText,
            opacity: POINTER_SHADOW_OPACITY,
          },
        },
        backgroundColor: palette.tooltipBackground,
        borderColor: palette.tooltipBorder,
        borderWidth: 1,
        padding: [6, 10],
        textStyle: { color: palette.tooltipText, fontSize: 12 },
        extraCssText:
          "border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);",
        valueFormatter: (value) => {
          const point = Array.isArray(value) ? value[0] : value;
          return typeof point === "number"
            ? `${point.toFixed(1)}%`
            : String(point ?? "");
        },
      } satisfies TooltipComponentOption,
      xAxis: {
        type: "category",
        data: buckets.map((bucket) => formatBucketDate(bucket.date)),
        axisLabel: {
          color: palette.label,
          fontSize: 11,
          interval: Math.max(0, Math.floor(buckets.length / 8) - 1),
          rotate: buckets.length > 14 ? 35 : 0,
        },
        axisLine: { lineStyle: { color: palette.axisLine } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        axisLabel: {
          color: palette.label,
          fontSize: 11,
          formatter: "{value}%",
        },
        splitLine: { lineStyle: { color: palette.splitLine } },
      },
      series: [
        {
          type: "bar",
          data: buckets.map((bucket) => bucket.occupancy_percentage),
          itemStyle: {
            color: palette.bar,
            borderRadius: [3, 3, 0, 0],
          },
          barMaxWidth: 24,
        },
      ],
    };
  }, [buckets, resolvedTheme]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || buckets.length === 0) {
      return;
    }

    const chart = echarts.init(container);
    chart.setOption(option);

    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [buckets, option]);

  return (
    <div
      ref={containerRef}
      className="h-64 w-full rounded-xl border border-surface-200 bg-surface-0 p-2 shadow-card"
      role="img"
      aria-label={ariaLabel}
    />
  );
}
